import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { NotificationResponse } from '../schemas/notification';

const getNotifications = async (skip = 0, limit = 20): Promise<NotificationResponse[]> => {
    const { data } = await api.get(`/notifications/?skip=${skip}&limit=${limit}`);
    return data;
};

const getUnreadNotificationCount = async (): Promise<number> => {
    const { data } = await api.get('/notifications/unread/count');
    return data;
};

const markAsRead = async (notificationId: string) => {
    const { data } = await api.post(`/notifications/${notificationId}/read`);
    return data;
};

const markAllAsRead = async () => {
    const { data } = await api.post('/notifications/read/all');
    return data;
};

export const useNotifications = () => {
    const queryClient = useQueryClient();

    const { data: notifications, isLoading: isLoadingNotifications } = useQuery({
        queryKey: ['notifications'],
        queryFn: () => getNotifications(),
        refetchInterval: 5000,
    });

    const { data: unreadCount, isLoading: isLoadingUnreadCount } = useQuery({
        queryKey: ['notifications', 'unread', 'count'],
        queryFn: getUnreadNotificationCount,
        refetchInterval: 5000,
    });

    const markAsReadMutation = useMutation({
        mutationFn: markAsRead,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notifications', 'unread', 'count'] });
        },
    });

    const markAllAsReadMutation = useMutation({
        mutationFn: markAllAsRead,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
            queryClient.invalidateQueries({ queryKey: ['notifications', 'unread', 'count'] });
        },
    });

    return {
        notifications,
        isLoadingNotifications,
        unreadCount,
        isLoadingUnreadCount,
        markAsRead: markAsReadMutation.mutate,
        markAllAsRead: markAllAsReadMutation.mutate,
    };
};
